import './index.scss';

import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  offset,
  Placement,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import classNames from 'classnames';
import { useRef, useState } from 'react';

import { SmartFloatingPortal as FloatingPortal } from '@/shared/ui/SmartFloatingPortal';

export enum TOOLTIP_STYLES {
  common = 'mm_tooltip_common',
}

interface ITooltipProps {
  triggerProps?: React.HTMLAttributes<HTMLDivElement>;
  contentProps?: React.HTMLAttributes<HTMLDivElement>;
  arrowProps?: React.RefAttributes<SVGSVGElement>;
  trigger: React.ReactNode;
  content?: React.ReactNode;
  options?: { placement: Placement | undefined; offset: number };
  style?: TOOLTIP_STYLES;
  inline?: boolean;
}

const Tooltip: React.FC<ITooltipProps> = ({
  trigger,
  triggerProps,
  content,
  contentProps,
  arrowProps,
  style,
  options = { placement: undefined, offset: 0 },
  inline = false,
}) => {
  const arrowRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const { refs, context, floatingStyles } = useFloating({
    open: isOpen,
    placement: options.placement,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(options.offset),
      flip({ padding: 8 }),
      shift({ padding: 5, crossAxis: true }),
      arrow({ element: arrowRef }),
    ],
  });

  const hover = useHover(context);
  const dissmiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });
  const focus = useFocus(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, dissmiss, role, focus]);

  return (
    <>
      <div
        className={classNames(style ?? TOOLTIP_STYLES.common, {
          mm_tooltip_common__trigger_inline: inline,
        })}
        ref={refs.setReference}
        {...triggerProps}
        {...getReferenceProps()}
      >
        {trigger}
      </div>
      {isOpen && content && (
        <FloatingPortal>
          <div
            className={(style ?? TOOLTIP_STYLES.common).concat('__content')}
            ref={refs.setFloating}
            {...contentProps}
            style={{ ...floatingStyles, zIndex: 1106 }}
            {...getFloatingProps()}
          >
            <FloatingArrow
              className={(style ?? TOOLTIP_STYLES.common).concat('__arrow')}
              {...arrowProps}
              ref={arrowRef}
              context={context}
            />
            {content}
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

export { Tooltip };
